import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { OnGroupDataMessageArgs, WebPubSubClient } from '@azure/web-pubsub-client';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import { eOrderType, HubService, Notification, NotificationService, Order, CANCEL_ON_NAVIGATION } from 'reg-hub-common';
import { Constants } from '../../../../constants';
import { EnvironmentUrlService } from '../../environment-url/environment-url.service';
import { Router } from '@angular/router';

const START_RETRY_DELAY_MS = 10000;

// Matches the API's OrderHubEventDTO, which is serialized with string enums
interface OrderHubEvent {
  eventType: 'Completed' | 'InProgress';
  order: Order;
}

@Injectable({
  providedIn: 'root'
})
export class OrdersHubService extends HubService implements OnDestroy {
  private client: WebPubSubClient | undefined;

  // Subjects for order events. These fire for every order the user can read, so subscribers filter by order ID.
  private completedOrderSubject = new Subject<Order>();
  public completedOrder$ = this.completedOrderSubject.asObservable();

  private inProgressOrderSubject = new Subject<Order>();
  public inProgressOrder$ = this.inProgressOrderSubject.asObservable();

  // Orders this user submitted in this session, which get a notification when they progress or complete
  private watchedOrders = new Map<string, { notification: Notification; watchInProgress: boolean; }>();

  constructor(private http: HttpClient,
    private environmentUrl: EnvironmentUrlService,
    private notificationService: NotificationService,
    private router: Router) { super(); }

  connect(): Observable<void> {
    if (!this.client) {
      this.client = new WebPubSubClient({
        // Negotiate on every (re)connect since access URLs expire. The API joins the connection to the
        // groups for each user group the user can read when issuing the URL, so there's no group to join here.
        getClientAccessUrl: async () => {
          const response = await firstValueFrom(
            this.http.get<{ url: string }>(`${this.environmentUrl.urlAddress}${Constants.ordersHubNegotiateUrl}`, { context: new HttpContext().set(CANCEL_ON_NAVIGATION, false) }));
          return response.url;
        }
      }, {
        // Each reconnect attempt negotiates with the API, so back off instead of retrying every second
        reconnectRetryOptions: { mode: "Exponential" }
      });

      this.client.on("group-message", (messageEvent) => {
        this.handleGroupMessageReceived(messageEvent);
      });

      this.client.on("disconnected", () => {
        console.warn("Disconnected from Web PubSub");
      });

      this.start();
    }

    return of(void 0);
  }

  // Once started, the client reconnects by itself, but a failed start (e.g. the negotiate fails) is never retried
  private start() {
    this.client?.start()
      .catch(err => {
        console.error('Error starting client', err);
        setTimeout(() => this.start(), START_RETRY_DELAY_MS);
      });
  }

  watchOrder(order: Order, options?: { shouldViewOrderOpenNewTab?: boolean; }) {
    if (this.watchedOrders.has(order.id)) {
      return;
    }

    this.watchedOrders.set(order.id, {
      notification: {
        id: 0,
        order: order,
        shouldNavigationOpenTab: options?.shouldViewOrderOpenNewTab ?? false
      },
      watchInProgress: order.orderTypeID == eOrderType.ultimateBeneficialOwnershipReport
    });
  }

  handleGroupMessageReceived(messageEvent: OnGroupDataMessageArgs) {
    if (messageEvent.message.dataType === "text") {
      const orderEvent = JSON.parse(messageEvent.message.data as string, this.toCamelCase) as OrderHubEvent;
      const order = orderEvent.order;
      const isCompleted = orderEvent.eventType === 'Completed';

      if (isCompleted) {
        this.completedOrderSubject.next(order);
      } else {
        this.inProgressOrderSubject.next(order);
      }

      const watched = this.watchedOrders.get(order.id);

      if (!watched || (!isCompleted && !watched.watchInProgress)) {
        return;
      }

      if (this.router.url !== `/orders/${order.id}`) {
        this.notificationService.addNotification(watched.notification);
      }

      // Only the first in progress update gets a notification; completion ends the watch
      if (isCompleted) {
        this.watchedOrders.delete(order.id);
      } else {
        watched.watchInProgress = false;
      }
    }
  }

  ngOnDestroy() {
    // Stop the connection if the service is destroyed (e.g., in certain testing scenarios)
    this.client?.stop();
    this.client = undefined;
  }
}
